import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PRESENCE_MERGE, PRESENCE_CHANGED, PRESENCE_RESET } from '../store/constants';

/** The status strings the server sends. Mirrors `constants.userOnlineStatus`. */
export const PRESENCE = {
  ONLINE: 'ONLINE',
  AWAY: 'AWAY',
  OFFLINE: 'OFFLINE',
  LOGOUT: 'LOGOUT',
};

export const presenceLabel = (status) => {
  if (status === PRESENCE.ONLINE) return 'Online';
  if (status === PRESENCE.AWAY) return 'Away';
  return 'Offline';
};

/**
 * Watch specific people's presence, and read it back.
 *
 * Presence used to arrive as `online:user-list` — the server's entire user-id → socket-id
 * map, `io.emit`ed to every client on every connect and disconnect anywhere. Each
 * component that cared kept its own `useState` copy and filtered the firehose itself.
 * That was an enumeration of the whole signed-in population handed to anyone with an
 * account, an O(users) payload delivered O(users) times, and — because it only ever
 * arrived as a CHANGE — no client could learn the current state of anyone without
 * waiting for an unrelated user to connect.
 *
 * The contract is now a subscription. `presence:subscribe` authorises the ask (you may
 * watch people you share a non-secret channel with, or have in contacts), returns their
 * current status in the ack, and joins this socket to a room per watched user so later
 * changes are one small `presence:changed` push. The ack is what seeds the store, so a
 * conversation opened with an online peer shows them online immediately.
 *
 * Every consumer shares one Redux map, so the sidebar row and the conversation header
 * cannot disagree about the same person.
 *
 * @param socket    the shared socket (may be null before it exists)
 * @param userIds   whoever is on screen. Order is irrelevant; the set is what matters.
 */
export const usePresence = (socket, userIds) => {
  const dispatch = useDispatch();
  const byUser = useSelector((state) => state?.PresenceReducer?.byUser);

  // The set as a stable primitive, so an array rebuilt on every render — which is what
  // a `.map()` in a component body produces — does not re-subscribe on every render.
  const key = useMemo(() => {
    const ids = [...new Set((userIds ?? []).filter(Boolean).map(String))];
    ids.sort();
    return ids.join(',');
  }, [userIds]);

  /**
   * Ids this socket is already in the room for. Subscribing is idempotent server-side,
   * but re-sending the whole set on every change would re-run its authorisation queries
   * for people already covered — and the sidebar's set grows by a page at a time.
   */
  const subscribed = useRef(new Set());

  /**
   * Bumped whenever every subscription this hook holds becomes void: rooms are joined per
   * SOCKET, so a replaced or reconnected socket is in none of them.
   *
   * It has to be state rather than just clearing the ref. Mutating a ref does not re-run
   * an effect, so clearing it from inside the `connect` handler would have left the hook
   * believing it was still subscribed to everything — nothing would re-subscribe after a
   * reconnect, silently, and every dot would freeze at whatever it last showed.
   */
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    subscribed.current = new Set();
    if (!socket) dispatch({ type: PRESENCE_RESET });
  }, [socket, dispatch]);

  useEffect(() => {
    if (!socket) return;

    const ids = key ? key.split(',') : [];
    const fresh = ids.filter((id) => !subscribed.current.has(id));
    if (fresh.length === 0) return;

    // Optimistically recorded: two panes mounting in the same tick must not both send
    // the same ids. A failed ack leaves them recorded, which is correct — the room join
    // is what matters and the server logs the failure; the next reconnect clears this.
    fresh.forEach((id) => subscribed.current.add(id));

    socket.emit('presence:subscribe', { user_ids: fresh }, (response) => {
      if (response?.presence) dispatch({ type: PRESENCE_MERGE, payload: response.presence });
    });
  }, [socket, key, epoch, dispatch]);

  useEffect(() => {
    if (!socket) return undefined;

    const onChanged = (payload) => dispatch({ type: PRESENCE_CHANGED, payload });

    /**
     * A reconnect means fresh rooms, so the whole subscription has to be re-sent, and the
     * map has to be dropped: a presence map held across a disconnect is a claim about live
     * connections made by a client that had none.
     */
    const onConnect = () => {
      subscribed.current = new Set();
      dispatch({ type: PRESENCE_RESET });
      setEpoch((value) => value + 1);
    };

    // Named handlers, removed by reference. The socket is shared app-wide, so a bare
    // `off('presence:changed')` would strip every other consumer's listener too.
    socket.on('presence:changed', onChanged);
    socket.on('connect', onConnect);

    return () => {
      socket.off('presence:changed', onChanged);
      socket.off('connect', onConnect);
    };
  }, [socket, dispatch]);

  /**
   * `undefined` for an id we have not heard about yet — deliberately not OFFLINE.
   * Callers that render a dot should treat unknown as offline; callers that render text
   * can tell "offline" from "not loaded" and avoid asserting either.
   */
  const statusOf = useCallback((userId) => byUser?.[userId]?.status, [byUser]);

  const isOnline = useCallback((userId) => byUser?.[userId]?.status === PRESENCE.ONLINE, [byUser]);

  /**
   * The `last_seen` the server is willing to disclose, which is null once the owner has
   * turned Last Seen off. Pushed with each change, so it stays current without a refetch.
   */
  const lastSeenOf = useCallback((userId) => byUser?.[userId]?.last_seen ?? null, [byUser]);

  return { byUser, statusOf, isOnline, lastSeenOf };
};

export default usePresence;
